import { initializeApp } from 'firebase/app';
import { getFirestore, collection, getDocs, addDoc, updateDoc, doc, getDoc, arrayUnion, setDoc, orderBy, query,   where } from 'firebase/firestore';
import { getAuth, signInAnonymously, onAuthStateChanged } from 'firebase/auth';
import { getStorage } from 'firebase/storage';
import { getDatabase } from 'firebase/database';
import { getMessaging, getToken, onMessage, isSupported } from 'firebase/messaging';

const firebaseConfig = {
  apiKey: "AIzaSyApqg1eUjbt0ZBzn3JNEPtfLz6gI4314xQ",
  // Вход через Google идёт через сам сайт (vercel.json проксирует /__/auth),
  // поэтому Google показывает rv2class.com, а не tracking-budget-app.
  authDomain: typeof window !== 'undefined' && /(^|\.)rv2class\.com$/.test(window.location.hostname)
    ? window.location.hostname
    : "tracking-budget-app.firebaseapp.com",
  databaseURL: "https://tracking-budget-app-default-rtdb.firebaseio.com",
  projectId: "tracking-budget-app",
  storageBucket: "tracking-budget-app.appspot.com",
  messagingSenderId: "912992088190",
  appId: "1:912992088190:web:926c8826b3bc39e2eb282f"
};

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);
const database = getDatabase(app);
const storage = getStorage(app);

// Promise that resolves ONLY when a user is authenticated (anonymous user ensured)
// All users in the app are anonymous, so this ensures anonymous auth is ready
export const authReady = new Promise((resolve) => {
  let resolved = false;
  
  // Listen to auth state changes - this is the primary way we detect successful auth
  const unsubscribe = onAuthStateChanged(auth, async (user) => {
    if (user && !resolved) {
      // User is authenticated (anonymous)
      resolved = true;
      console.log('✅ Anonymous authentication ready - User ID:', user.uid);
      unsubscribe();
      resolve();
    } else if (!user && !resolved) {
      // No user yet, try to sign in anonymously
      try {
        await signInAnonymously(auth);
        // onAuthStateChanged will fire again with the user, which will resolve the promise
      } catch (error) {
        console.error('❌ Anonymous auth failed:', error);
        // Retry after a short delay
        setTimeout(async () => {
          if (!resolved && !auth.currentUser) {
            try {
              await signInAnonymously(auth);
            } catch (retryError) {
              console.error('❌ Anonymous auth retry failed:', retryError);
            }
          }
        }, 1000);
      }
    }
  });
  
  // If there's already a user, resolve immediately
  if (auth.currentUser) {
    resolved = true;
    console.log('✅ Anonymous authentication already ready - User ID:', auth.currentUser.uid);
    unsubscribe();
    resolve();
  }
});

const VAPID_KEY = 'BMhBmJi2UwvHoXaJs9ScmykAYqC7pQCaXpF5qAtNc_0pHNEMKEl7Y4Qrb-abrotyE7aERv7YXld0ehmcYBIVL7o';

let messaging;
const initializeMessaging = async () => {
  try {
    if (await isSupported()) {
      messaging = getMessaging(app);
      return true;
    } else {
      console.log('Firebase messaging is not supported in this browser');
      return false;
    }
  } catch (error) {
    console.error('Error initializing messaging:', error);
    return false;
  }
};

// Initialize messaging
initializeMessaging();

const logError = async (userId, error, context) => {
  try {
    await addDoc(collection(db, 'errors'), {
      userId,
      error: error.message,
      context,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
    });
    console.log('Error logged successfully');
  } catch (logError) {
    console.error('Failed to log error:', logError);
  }
};

// In firebase.js
export const requestNotificationPermission = async () => {
  try {
    // First check if notifications are supported
    if (!('Notification' in window)) {
      throw new Error('This browser does not support notifications');
    }

    // Check if we already have permission
    if (Notification.permission === 'granted') {
      // Get the token directly if we already have permission
      return await getToken(messaging);
    }

    // If permission is denied, error out early
    if (Notification.permission === 'denied') {
      throw new Error('Notification permission was denied');
    }

    // Request permission if it's not granted or denied
    const permission = await Notification.requestPermission();
    
    if (permission === 'granted') {
      // Get the token after permission is granted
      return await getToken(messaging);
    } else {
      throw new Error('Notification permission was not granted');
    }
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    throw error; // Re-throw to be handled by the calling function
  }
};

export const updateUserFCMToken = async (userId, token) => {
  try {
    const userRef = doc(db, 'students', userId);
    const userDoc = await getDoc(userRef);
    
    // Generate a unique device ID if not exists
    const deviceId = localStorage.getItem('deviceId') || 
      `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('deviceId', deviceId);

    const deviceInfo = {
      platform: navigator.platform,
      browser: navigator.userAgent.split(' ').pop(),
      deviceType: /Mobile|Android|iPhone/i.test(navigator.userAgent) ? 'mobile' : 'desktop',
      language: navigator.language,
      lastSeen: new Date().toISOString()
    };

    const updateData = {
      [`devices.${deviceId}`]: {
        fcmToken: token,
        enabled: true,
        deviceInfo,
        lastTokenUpdate: new Date().toISOString()
      },
      lastUpdated: new Date().toISOString()
    };

    if (userDoc.exists()) {
      await updateDoc(userRef, updateData);
      console.log('Device token updated successfully');
    } else {
      await setDoc(userRef, {
        devices: {
          [deviceId]: {
            fcmToken: token,
            enabled: true,
            deviceInfo,
            lastTokenUpdate: new Date().toISOString()
          }
        },
        lastUpdated: new Date().toISOString()
      });
      console.log('New user document created with device token');
    }

    return deviceId;
  } catch (error) {
    console.error('Error updating device token:', error);
    await logError(userId, error, 'updateUserFCMToken');
    throw error;
  }
};
export const onMessageListener = () => {
  if (!messaging) {
    return Promise.reject(new Error('Messaging is not supported in this browser'));
  }

  return new Promise((resolve) => {
    onMessage(messaging, (payload) => {
      console.log('Received foreground message:', payload);
      
      // Update notification status if notificationId exists
      if (payload.data?.notificationId) {
        updateNotificationStatus(payload.data.notificationId, 'delivered');
      }
      
      resolve(payload);
    });
  });
};

export const updateNotificationStatus = async (notificationId, status) => {
  try {
    const deviceId = localStorage.getItem('deviceId');
    if (!deviceId) {
      throw new Error('Device ID not found');
    }

    const notificationRef = doc(db, 'notifications', notificationId);
    const updateData = {
      status,
      [`devices.${deviceId}.status`]: status,
      [`devices.${deviceId}.${status}At`]: new Date().toISOString(),
      [`devices.${deviceId}.deviceInfo`]: {
        platform: navigator.platform,
        browser: navigator.userAgent.split(' ').pop(),
        deviceType: /Mobile|Android|iPhone/i.test(navigator.userAgent) ? 'mobile' : 'desktop',
        language: navigator.language
      },
      lastUpdated: new Date().toISOString()
    };

    await updateDoc(notificationRef, updateData);
    console.log(`Notification ${notificationId} status updated to ${status}`);
  } catch (error) {
    console.error(`Error updating notification status to ${status}:`, error);
    await logError(null, error, 'updateNotificationStatus');
  }
};

export const handlePermissionChange = async (userId) => {
  const deviceId = localStorage.getItem('deviceId');
  if (!deviceId) return;

  try {
    const userRef = doc(db, 'students', userId);
    const permission = Notification.permission;

    await updateDoc(userRef, {
      [`devices.${deviceId}.enabled`]: permission === 'granted',
      [`devices.${deviceId}.lastPermissionUpdate`]: new Date().toISOString(),
      lastUpdated: new Date().toISOString()
    });
  } catch (error) {
    console.error('Error updating permission status:', error);
    await logError(userId, error, 'handlePermissionChange');
  }
};

export const fetchNotifications = async (userId) => {
  try {
    const notificationsQuery = query(
      collection(db, 'notifications'),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc')
    );
    
    const querySnapshot = await getDocs(notificationsQuery);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error fetching notifications:', error);
    await logError(userId, error, 'fetchNotifications');
    return [];
  }
};

export const loadInitialNotifications = async (userId) => {
  try {
    const notifications = await fetchNotifications(userId);
    return notifications;
  } catch (error) {
    console.error('Error loading initial notifications:', error);
    await logError(userId, error, 'loadInitialNotifications');
    return [];
  }
};

export const saveNotificationToFirestore = async (userId, notification) => {
  try {
    const docRef = await addDoc(collection(db, 'notifications'), {
      userId,
      ...notification,
      createdAt: new Date().toISOString(),
      status: 'created'
    });
    console.log('Notification saved to Firestore successfully');
    return docRef.id;
  } catch (error) {
    console.error('Error saving notification to Firestore:', error);
    await logError(userId, error, 'saveNotificationToFirestore');
    return null;
  }
};

export const fetchCourseTree = async () => {
  try {
    const querySnapshot = await getDocs(collection(db, 'library'));
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error fetching course tree:', error);
    await logError(null, error, 'fetchCourseTree');
    return [];
  }
};

export const saveProgressToFirebase = async (studentId, progressData) => {
  try {
    const docRef = await addDoc(collection(db, 'progress'), {
      studentId,
      progressData,
      timestamp: new Date().toISOString()
    });
    console.log('Progress saved successfully');
    return docRef.id;
  } catch (error) {
    console.error('Error saving progress:', error);
    await logError(studentId, error, 'saveProgressToFirebase');
    throw error;
  }
};

 // 1. Add proper indexing in Firestore
 const createIndexes = async () => {
  try {
    const indexesRef = doc(db, 'tableData', 'indexes');
    await setDoc(indexesRef, {
      studentId_timestamp: true,
      courseId_lessonId: true
    });
    console.log('Indexes created successfully');
  } catch (error) {
    console.error('Error creating indexes:', error);
    await logError(null, error, 'createIndexes');
  }
};



export { db, auth, storage, database, messaging };